import { normalizeSearch, raAppliesTo } from '@felix/domain';
import type { RaDefaultActivity, RaDefaultLibraryView } from './types.js';

/**
 * Risk ▸ Master Data ▸ Default library'nin saf yardımcıları (ADR 0106 §2): süzgeç ve seçim.
 * Alan seçimi alt alanları da kapsar (RA-05 → RA-05-01 …); gemi tipi uygunluğu ana verinin
 * kuralıdır (ALL her tipe uygulanır; ADR 0086 §4).
 */

export interface DefaultLibraryFilter {
  query: string;
  /** Area ID; `all` ya da alan (alt alanlarıyla). */
  area: string;
  /** Parent Node (System / Component). */
  node: string;
  /** Gemi grubu. */
  group: string;
  show: 'all' | 'available' | 'inLibrary';
}

export const DEFAULT_LIBRARY_FILTER: DefaultLibraryFilter = {
  query: '',
  area: 'all',
  node: 'all',
  group: 'all',
  show: 'all',
};

const inArea = (activity: RaDefaultActivity, area: string) =>
  activity.areaCode === area || activity.areaCode.startsWith(`${area}-`);

export function filterDefaultLibrary(
  view: RaDefaultLibraryView,
  filter: DefaultLibraryFilter,
): RaDefaultActivity[] {
  const query = normalizeSearch(filter.query.trim());
  return view.activities.filter((a) => {
    if (filter.area !== 'all' && !inArea(a, filter.area)) return false;
    if (filter.node !== 'all' && a.nodeCode !== filter.node) return false;
    if (filter.group !== 'all' && !raAppliesTo(a.vesselGroups, filter.group)) return false;
    if (filter.show === 'available' && a.inLibrary) return false;
    if (filter.show === 'inLibrary' && !a.inLibrary) return false;
    if (query === '') return true;
    return normalizeSearch(
      [a.code, a.name, a.nodeName, a.areaName, ...a.hazards].join(' '),
    ).includes(query);
  });
}

/** Düğüm seçenekleri: seçilen alanın (alt alanlarıyla) düğümleri. */
export function nodeOptions(
  view: RaDefaultLibraryView,
  area: string,
): RaDefaultLibraryView['nodes'] {
  return view.nodes.filter(
    (n) => area === 'all' || n.areaCode === area || n.areaCode.startsWith(`${area}-`),
  );
}

/** Seçilebilir (şirketin kütüphanesinde olmayan) faaliyetlerin kodları. */
export function selectableCodes(activities: readonly RaDefaultActivity[]): string[] {
  return activities.filter((a) => !a.inLibrary).map((a) => a.code);
}
